import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useId, useState, type KeyboardEvent } from 'react';

export interface CommittentiFieldProps {
  value: string[];
  onChange: (value: string[]) => void;
  error?: string;
  disabled?: boolean;
}

const HELPER =
  'Come li dichiara il prodotto nel payload delle segnalazioni: un ticket con un committente non in elenco è rifiutato (403).';

/** Committenti autorizzati come elenco di chip: si aggiunge con Invio o «Aggiungi», si toglie dal chip. */
export function CommittentiField({ value, onChange, error, disabled = false }: CommittentiFieldProps) {
  const [draft, setDraft] = useState('');
  const listId = useId();

  const add = () => {
    const committente = draft.trim();
    if (!committente) return;
    if (!value.includes(committente)) onChange([...value, committente]);
    setDraft('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      add();
    }
  };

  return (
    <FormControl component="fieldset" error={Boolean(error)} disabled={disabled} sx={{ width: '100%' }}>
      <Stack direction="row" spacing={2} alignItems="flex-start">
        <TextField
          label="Committente autorizzato"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={onKeyDown}
          disabled={disabled}
          error={Boolean(error)}
          fullWidth
          slotProps={{ htmlInput: { 'aria-describedby': listId } }}
        />
        <Button
          startIcon={<AddIcon />}
          variant="outlined"
          onClick={add}
          disabled={disabled || !draft.trim()}
          sx={{ mt: 1 }}
        >
          Aggiungi
        </Button>
      </Stack>
      <Stack
        id={listId}
        component="ul"
        role="list"
        aria-label="Committenti autorizzati"
        direction="row"
        flexWrap="wrap"
        gap={1}
        sx={{ listStyle: 'none', p: 0, m: 0, mt: value.length > 0 ? 2 : 0 }}
      >
        {value.map((committente) => (
          <Box component="li" key={committente}>
            <Chip
              label={committente}
              onDelete={disabled ? undefined : () => onChange(value.filter((c) => c !== committente))}
              deleteIcon={<CancelIcon aria-label={`Rimuovi ${committente}`} />}
            />
          </Box>
        ))}
      </Stack>
      <FormHelperText>{error ?? HELPER}</FormHelperText>
    </FormControl>
  );
}
